import React, { useState, useEffect, useCallback } from 'react';
import { getSwingScanResults, generateSwingPortfolio, createSwingPosition, getSwingPositions, applySwingPortfolio, getSwingSettings } from '../../api/client';
import TradeChecklist, { computeChecks } from './TradeChecklist';
import useMediaQuery from '../../hooks/useMediaQuery';
import MobileCard, { MobileCardHeader, MobileCardBody, MobileCardActions, MobileKV } from './MobileCard';

const SETUP_TAGS = ['ALL', 'BREAKOUT', 'PULLBACK', 'RETEST', 'RS_LEADER'];

const TAG_COLORS = {
  BREAKOUT:  { bg: '#1a3a1a', color: '#4CAF50', border: '#2d6a2d' },
  PULLBACK:  { bg: '#1a2a3a', color: '#2196F3', border: '#1e4a7a' },
  RETEST:    { bg: '#3a2a1a', color: '#FF9800', border: '#7a4a1e' },
  RS_LEADER: { bg: '#2a1a3a', color: '#9C27B0', border: '#5a2a7a' },
};

function ScoreBar({ score, color = '#4CAF50' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, background: '#2a2a2a', borderRadius: 4, height: 8, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: `${Math.min(100, score)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, color: '#aaa', minWidth: 26, textAlign: 'right' }}>{score}</span>
    </div>
  );
}

function TagBadge({ tag }) {
  const style = TAG_COLORS[tag] || { bg: '#2a2a2a', color: '#aaa', border: '#444' };
  return (
    <span style={{
      padding: '2px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600,
      background: style.bg, color: style.color, border: `1px solid ${style.border}`,
    }}>
      {tag}
    </span>
  );
}

function BreakdownPanel({ result, checklistCtx }) {
  const scores = [
    { label: 'Trend',      value: result.trend_score    || 0, color: '#4CAF50', weight: '25%' },
    { label: 'Momentum',   value: result.momentum_score || 0, color: '#2196F3', weight: '25%' },
    { label: 'Mean Rev.',  value: result.mean_rev_score || 0, color: '#FF9800', weight: '15%' },
    { label: 'Rel. Str.',  value: result.rs_score       || 0, color: '#9C27B0', weight: '20%' },
    { label: 'S/R Retest', value: result.sr_score       || 0, color: '#F44336', weight: '15%' },
  ];
  return (
    <tr>
      <td colSpan={12} style={{ padding: '0 12px 12px 48px', background: '#141414' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, paddingTop: 10 }}>
          {scores.map(s => (
            <div key={s.label}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{s.label} <span style={{ color: '#555' }}>({s.weight})</span></div>
              <ScoreBar score={s.value} color={s.color} />
            </div>
          ))}
          {result.notes && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#888', marginTop: 4, fontStyle: 'italic' }}>
              {result.notes}
            </div>
          )}
        </div>
        <FundamentalsPanel result={result} />
        <div style={{ marginTop: 12 }}>
          <TradeChecklist result={result} ctx={checklistCtx} />
        </div>
      </td>
    </tr>
  );
}

/**
 * Mobile breakdown panel — same content as BreakdownPanel but rendered as a
 * plain <div> so it can sit inside a MobileCard instead of a <table>.
 */
function BreakdownPanelMobile({ result, checklistCtx }) {
  const scores = [
    { label: 'Trend',      value: result.trend_score    || 0, color: '#4CAF50', weight: '25%' },
    { label: 'Momentum',   value: result.momentum_score || 0, color: '#2196F3', weight: '25%' },
    { label: 'Mean Rev.',  value: result.mean_rev_score || 0, color: '#FF9800', weight: '15%' },
    { label: 'Rel. Str.',  value: result.rs_score       || 0, color: '#9C27B0', weight: '20%' },
    { label: 'S/R Retest', value: result.sr_score       || 0, color: '#F44336', weight: '15%' },
  ];
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 12 }}>
        {scores.map(s => (
          <div key={s.label}>
            <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>
              {s.label} <span style={{ color: '#555' }}>({s.weight})</span>
            </div>
            <ScoreBar score={s.value} color={s.color} />
          </div>
        ))}
      </div>
      <FundamentalsPanel result={result} />
      <div style={{ marginTop: 12 }}>
        <TradeChecklist result={result} ctx={checklistCtx} />
      </div>
    </div>
  );
}

function FundamentalsPanel({ result }) {
  const hasData = result.pe_ratio != null || result.roe != null || result.profit_margins != null;
  if (!hasData) {
    return (
      <div style={{ marginTop: 10, color: '#555', fontSize: 11 }}>
        Fundamental data not available (yfinance coverage varies for NSE stocks)
      </div>
    );
  }

  const fmtPct = v => v != null ? `${(v * 100).toFixed(1)}%` : '—';
  const fmtNum = (v, d = 1) => v != null ? v.toFixed(d) : '—';
  const fmtCr  = v => v != null ? `₹${Math.round(v / 1e7).toLocaleString('en-IN')} Cr` : '—';

  const bonus = result.fundamental_bonus ?? 0;
  const bonusPct = Math.round(bonus * 10);  // scale to 0-100 for bar

  const metrics = [
    { label: 'P/E (TTM)',    value: fmtNum(result.pe_ratio),       good: result.pe_ratio != null && result.pe_ratio < 40 },
    { label: 'P/B',          value: fmtNum(result.pb_ratio),       good: result.pb_ratio != null && result.pb_ratio < 4 },
    { label: 'ROE',          value: fmtPct(result.roe),            good: result.roe != null && result.roe > 0.15 },
    { label: 'D/E',          value: fmtNum(result.debt_to_equity), good: result.debt_to_equity != null && result.debt_to_equity < 0.75 },
    { label: 'Rev. Growth',  value: fmtPct(result.revenue_growth), good: result.revenue_growth != null && result.revenue_growth > 0.10 },
    { label: 'EPS Growth',   value: fmtPct(result.earnings_growth),good: result.earnings_growth != null && result.earnings_growth > 0.15 },
    { label: 'Net Margin',   value: fmtPct(result.profit_margins), good: result.profit_margins != null && result.profit_margins > 0.12 },
    { label: 'Mkt Cap',      value: fmtCr(result.market_cap),      good: true },
  ];

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid #2a2a2a', paddingTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>Fundamentals</span>
        <div style={{ flex: 1, height: 4, background: '#222', borderRadius: 2, overflow: 'hidden', maxWidth: 80 }}>
          <div style={{ width: `${bonusPct}%`, height: '100%', background: '#00BCD4', transition: 'width 0.3s' }} />
        </div>
        <span style={{ color: '#00BCD4', fontSize: 11 }}>+{bonus.toFixed(1)} pts bonus</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px 16px' }}>
        {metrics.map(m => (
          <div key={m.label} style={{ fontSize: 11 }}>
            <span style={{ color: '#555' }}>{m.label}: </span>
            <span style={{ color: m.good ? '#4CAF50' : '#ccc', fontWeight: m.good ? 600 : 400 }}>
              {m.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortfolioModal({ portfolio, onClose, onApplied, checklistCtx }) {
  const [applying, setApplying]   = useState(null);  // 'ACTIVE' | 'WATCHLIST' | null
  const [applyMsg, setApplyMsg]   = useState('');
  const [applyErr, setApplyErr]   = useState('');

  if (!portfolio) return null;
  const positions = portfolio.positions || [];

  const handleApply = async (targetStatus) => {
    if (!positions.length) return;
    setApplying(targetStatus);
    setApplyMsg('');
    setApplyErr('');
    try {
      const res = await applySwingPortfolio({ positions, target_status: targetStatus });
      const { added, skipped, message } = res.data;
      setApplyMsg(message || `${added} positions added to ${targetStatus}.`);
      if (onApplied) onApplied();   // refresh watchlisted set in parent
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setApplyErr(
        typeof detail === 'string' ? detail :
        Array.isArray(detail) ? detail.map(d => d.msg).join(', ') :
        'Failed to apply portfolio'
      );
    } finally {
      setApplying(null);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 10, padding: 24, maxWidth: 1100, width: '95%', maxHeight: '88vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Generated Portfolio</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {/* Summary row */}
        <div style={{ marginBottom: 12, fontSize: 13, color: '#888', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span>{positions.length} positions</span>
          <span>Deployed: ₹{(portfolio.total_deployed || 0).toLocaleString('en-IN')}</span>
          <span>Cash: ₹{(portfolio.cash_reserve || 0).toLocaleString('en-IN')}</span>
          <span style={{ color: '#4CAF50' }}>{portfolio.pct_deployed || 0}% deployed</span>
        </div>

        {/* Bulk-apply buttons */}
        {positions.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <button
              onClick={() => handleApply('WATCHLIST')}
              disabled={!!applying}
              style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid #1565C0',
                background: applying === 'WATCHLIST' ? '#0d47a1' : '#1a2a4a',
                color: '#64B5F6', cursor: applying ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {applying === 'WATCHLIST' ? '⏳' : '📥'} Add All to Watchlist
            </button>
            <button
              onClick={() => handleApply('ACTIVE')}
              disabled={!!applying}
              style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid #2E7D32',
                background: applying === 'ACTIVE' ? '#1b5e20' : '#1a3a1a',
                color: '#66BB6A', cursor: applying ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {applying === 'ACTIVE' ? '⏳' : '✅'} Add All to Active
            </button>
          </div>
        )}

        {/* Feedback */}
        {applyMsg && (
          <div style={{ marginBottom: 10, padding: '8px 12px', background: '#1a3a1a', border: '1px solid #2E7D32', borderRadius: 6, color: '#66BB6A', fontSize: 13 }}>
            ✓ {applyMsg}
          </div>
        )}
        {applyErr && (
          <div style={{ marginBottom: 10, padding: '8px 12px', background: '#3a1a1a', border: '1px solid #7d2e2e', borderRadius: 6, color: '#ef9a9a', fontSize: 13 }}>
            ✗ {applyErr}
          </div>
        )}

        {/* Positions table */}
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #333' }}>
              {['Symbol', 'Sector', 'Score', 'Ready', 'Entry ₹', 'SL ₹', 'T1 ₹', 'T2 ₹', 'Qty', '% Port'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {positions.map((p, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                <td style={{ padding: '8px 10px', fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{p.symbol}</td>
                <td style={{ padding: '8px 10px', color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>{p.sector || '—'}</td>
                <td style={{ padding: '8px 10px', color: '#4CAF50' }}>{Math.round(p.composite_score || 0)}</td>
                <td style={{ padding: '8px 10px' }}>
                  <TradeChecklist result={p} ctx={checklistCtx} compact />
                </td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{p.entry_price ? `₹${p.entry_price.toLocaleString('en-IN')}` : '—'}</td>
                <td style={{ padding: '8px 10px', color: '#f44', whiteSpace: 'nowrap' }}>{p.stop_loss ? `₹${p.stop_loss.toLocaleString('en-IN')}` : '—'}</td>
                <td style={{ padding: '8px 10px', color: '#4CAF50', whiteSpace: 'nowrap' }}>{p.target1 ? `₹${p.target1.toLocaleString('en-IN')}` : '—'}</td>
                <td style={{ padding: '8px 10px', color: '#4CAF50', whiteSpace: 'nowrap' }}>{p.target2 ? `₹${p.target2.toLocaleString('en-IN')}` : '—'}</td>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{p.qty_final || '—'}</td>
                <td style={{ padding: '8px 10px', color: '#888' }}>{p.pct_of_portfolio ? `${p.pct_of_portfolio}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {positions.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: '#666' }}>No picks generated — ensure stocks have valid entry price and stop loss from the scanner.</div>
        )}
      </div>
    </div>
  );
}

export default function SwingScanner() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [filterTag, setFilterTag]   = useState('ALL');
  const [minScore, setMinScore]     = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll]       = useState(false);   // default: top 50 only
  const DEFAULT_VISIBLE = 50;
  const [sortField, setSortField]   = useState('rank');
  const [sortDir, setSortDir]       = useState('asc');
  const [expanded, setExpanded]     = useState(null);
  const [portfolio, setPortfolio]   = useState(null);
  const [generating, setGenerating] = useState(false);
  const [addingRow, setAddingRow]   = useState(null);
  const [watchlisted, setWatchlisted] = useState(new Set());
  const [sectorDeployment, setSectorDeployment] = useState({});  // { sector: pct }
  const [settings, setSettings] = useState(null);

  // Load symbols already in active portfolio / watchlist + compute sector deployment %
  const loadWatchlisted = useCallback(async () => {
    try {
      const [active, watchlist, settingsRes] = await Promise.all([
        getSwingPositions({ status: 'ACTIVE' }),
        getSwingPositions({ status: 'WATCHLIST' }),
        getSwingSettings(),
      ]);
      const syms = new Set([
        ...(active.data.positions || []).map(p => p.symbol),
        ...(watchlist.data.positions || []).map(p => p.symbol),
      ]);
      setWatchlisted(syms);

      // Compute sector deployment % from ACTIVE positions
      const capital = settingsRes.data.capital || 0;
      const deployment = {};
      if (capital > 0) {
        for (const p of active.data.positions || []) {
          if (!p.sector || !p.entry_price || !p.quantity) continue;
          const pct = (p.entry_price * p.quantity / capital) * 100;
          deployment[p.sector] = (deployment[p.sector] || 0) + pct;
        }
      }
      setSectorDeployment(deployment);
      setSettings(settingsRes.data);
    } catch { /* non-fatal */ }
  }, []);

  // Build the context object passed to TradeChecklist
  const checklistCtx = {
    sectorDeployment,
    sectorCapPct: settings?.sector_cap_pct || 30,
    riskPct: settings?.risk_pct || 1.5,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch enough to cover the full scored universe (~280-350 on a good day).
      // The display still defaults to top 50 via the showAll toggle; this is
      // just the upstream cap. Set to 1000 so the cap is effectively "all".
      const params = { limit: 1000 };
      if (filterTag !== 'ALL') params.setup_tag = filterTag;
      if (minScore > 0) params.min_score = minScore;
      const res = await getSwingScanResults(params);
      setResults(res.data.results || []);
    } catch {
      setError('Failed to load scan results');
    } finally {
      setLoading(false);
    }
  }, [filterTag, minScore]);

  useEffect(() => { load(); loadWatchlisted(); }, [load, loadWatchlisted]);

  const handleGeneratePortfolio = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await generateSwingPortfolio({ min_score: minScore, setup_tag: filterTag !== 'ALL' ? filterTag : undefined });
      setPortfolio(res.data);
    } catch {
      setError('Failed to generate portfolio');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddWatchlist = async (result) => {
    // Pre-flight check — warn if readiness is below threshold
    const { readiness, passCount, total, checks } = computeChecks(result, checklistCtx);
    if (readiness === 'NOT_READY') {
      const failedLabels = checks.filter(c => !c.pass).map(c => `• ${c.label}`).join('\n');
      const proceed = window.confirm(
        `${result.symbol} is NOT READY (${passCount}/${total} checks passed).\n\n` +
        `Failed checks:\n${failedLabels}\n\n` +
        `Add to watchlist anyway?`
      );
      if (!proceed) return;
    }

    setAddingRow(result.symbol);
    try {
      await createSwingPosition({
        symbol: result.symbol,
        status: 'WATCHLIST',
        sector: result.sector,
        setup_tags: result.setup_tags || null,
        entry_price: result.entry_price || result.close_price || 0,
        stop_loss: result.stop_loss || 0,
        target1: result.target1 || null,
        target2: result.target2 || null,
      });
      setWatchlisted(prev => new Set([...prev, result.symbol]));
      setSuccess(`${result.symbol} added to watchlist`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to add to watchlist');
      setError(msg);
    } finally {
      setAddingRow(null);
    }
  };

  const scoreColor = (score) => {
    if (score >= 75) return '#4CAF50';
    if (score >= 50) return '#FF9800';
    return '#f44336';
  };

  const q = searchQuery.trim().toUpperCase();
  const searchFiltered = q
    ? results.filter(r => r.symbol.toUpperCase().includes(q) || (r.sector || '').toUpperCase().includes(q))
    : results;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'rank' ? 'asc' : 'desc');  // score/vol/52w default desc
    }
  };

  const sortedAll = [...searchFiltered].sort((a, b) => {
    const av = a[sortField] ?? (sortDir === 'asc' ? Infinity : -Infinity);
    const bv = b[sortField] ?? (sortDir === 'asc' ? Infinity : -Infinity);
    return sortDir === 'asc' ? av - bv : bv - av;
  });
  // Apply display cap unless user opted to see all. Search is unaffected
  // (search filters sortedAll BEFORE the cap), so finding a stock ranked
  // #80 still works even when the table is showing top 50.
  const truncated = !showAll && sortedAll.length > DEFAULT_VISIBLE && !q;
  const filteredResults = truncated ? sortedAll.slice(0, DEFAULT_VISIBLE) : sortedAll;

  return (
    <div>
      {error && <div style={{ color: '#f44', marginBottom: 12, padding: '8px 12px', background: '#2a1010', borderRadius: 6 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
      {success && <div style={{ color: '#4CAF50', marginBottom: 12, padding: '8px 12px', background: '#0a2010', borderRadius: 6 }}>{success}</div>}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Setup tag pills */}
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Setup</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {SETUP_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => setFilterTag(tag)}
                  style={{
                    padding: '4px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                    background: filterTag === tag ? '#4CAF50' : '#1e1e1e',
                    color: filterTag === tag ? '#fff' : '#aaa',
                    border: filterTag === tag ? '1px solid #4CAF50' : '1px solid #444',
                    fontWeight: filterTag === tag ? 600 : 400,
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Search box */}
          <div style={{ minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Search</div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 8, color: '#555', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
              <input
                type="text"
                placeholder="Symbol or sector…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  padding: '5px 8px 5px 28px', borderRadius: 6, border: '1px solid #444',
                  background: '#1e1e1e', color: '#fff', fontSize: 13, width: '100%', outline: 'none',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: 6, background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                >×</button>
              )}
            </div>
          </div>

          {/* Min Score slider */}
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Min Score: {minScore}</div>
            <input
              type="range" min={0} max={100} value={minScore}
              onChange={e => setMinScore(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#4CAF50' }}
            />
          </div>

          {/* Generate button */}
          <button
            onClick={handleGeneratePortfolio}
            disabled={generating || results.length === 0}
            style={{
              padding: '8px 18px', background: generating ? '#555' : '#FF9800',
              color: '#fff', border: 'none', borderRadius: 6,
              cursor: generating || results.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {generating ? 'Generating...' : '🎯 Generate Portfolio'}
          </button>
        </div>
      </div>

      {/* Results table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>Scan Results</span>
          <span style={{ color: '#888', fontSize: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Count label adapts to search / truncation state */}
            {q
              ? `${filteredResults.length} of ${results.length} stocks (filtered)`
              : truncated
                ? `Top ${DEFAULT_VISIBLE} of ${sortedAll.length} stocks`
                : `${sortedAll.length} stocks`}
            {/* Toggle: collapse to top 50 / expand to all */}
            {sortedAll.length > DEFAULT_VISIBLE && !q && (
              <button
                onClick={() => setShowAll(v => !v)}
                style={{
                  background: 'none', border: '1px solid #444', color: '#4CAF50',
                  padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                  fontSize: 11, fontWeight: 600,
                }}
              >
                {showAll ? `Show top ${DEFAULT_VISIBLE}` : `Show all ${sortedAll.length}`}
              </button>
            )}
          </span>
        </div>
        {loading ? (
          <div style={{ padding: 24, color: '#888', textAlign: 'center' }}>Loading scan results...</div>
        ) : results.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No scan results</div>
            <div style={{ fontSize: 13, color: '#555' }}>Run a scan from the Dashboard tab to populate results.</div>
          </div>
        ) : filteredResults.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 13 }}>
            No stocks match <strong style={{ color: '#aaa' }}>"{searchQuery}"</strong> — try a different symbol or sector name.
          </div>
        ) : isMobile ? (
          // ── Mobile: compact card list ──────────────────────────────────────
          <div style={{ padding: 10 }}>
            {filteredResults.map((r, i) => {
              const watching = watchlisted.has(r.symbol);
              const score = Math.round(r.composite_score || 0);
              const accent = score >= 75 ? '#4CAF50' : score >= 50 ? '#FF9800' : '#888';
              return (
                <MobileCard
                  key={r.symbol}
                  accent={accent}
                  onClick={() => setExpanded(expanded === r.symbol ? null : r.symbol)}
                >
                  <MobileCardHeader
                    symbol={`#${r.rank || i + 1} · ${r.symbol}`}
                    sector={r.sector}
                    right={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                        <span style={{ color: accent, fontWeight: 700, fontSize: 16 }}>{score}</span>
                        <TradeChecklist result={r} ctx={checklistCtx} compact />
                      </div>
                    }
                  />
                  <MobileCardBody cols={2}>
                    <MobileKV label="Close ₹" mono bold
                      value={r.close_price ? `₹${parseFloat(r.close_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null} />
                    <MobileKV label="RSI" mono
                      color={r.rsi_daily > 70 ? '#f44' : r.rsi_daily < 30 ? '#2196F3' : '#fff'}
                      value={r.rsi_daily ? parseFloat(r.rsi_daily).toFixed(1) : null} />
                    <MobileKV label="Vol Ratio" mono
                      color={r.volume_ratio >= 1.5 ? '#4CAF50' : r.volume_ratio < 0.8 ? '#f44' : '#aaa'}
                      value={r.volume_ratio != null ? `${parseFloat(r.volume_ratio).toFixed(2)}×` : null} />
                    <MobileKV label="52w High" mono
                      color={r.week52_high_pct >= -5 ? '#4CAF50' : r.week52_high_pct >= -15 ? '#FF9800' : '#888'}
                      value={r.week52_high_pct != null ? `${parseFloat(r.week52_high_pct).toFixed(1)}%` : null} />
                    <MobileKV label="EMA" value={
                      (() => {
                        const aligned = r.close_price && r.ema20 && r.ema50 && r.close_price > r.ema20 && r.ema20 > r.ema50;
                        return <span style={{ color: aligned ? '#4CAF50' : '#f44' }}>{aligned ? '✓ Aligned' : '✗ Mixed'}</span>;
                      })()
                    } />
                    <MobileKV label="Tags" value={
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {(r.setup_tags ? r.setup_tags.split(',') : []).filter(Boolean).slice(0, 2).map(t => (
                          <TagBadge key={t} tag={t.trim()} />
                        ))}
                      </div>
                    } />
                  </MobileCardBody>
                  <MobileCardActions>
                    {watching ? (
                      <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 600, padding: '6px 12px' }}>
                        ✓ Watching
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAddWatchlist(r); }}
                        disabled={addingRow === r.symbol}
                        style={{
                          padding: '8px 16px', fontSize: 12, background: '#0a2010',
                          border: '1px solid #2d6a2d', color: '#4CAF50', borderRadius: 6,
                          fontWeight: 600, cursor: 'pointer', flex: 1, minHeight: 36,
                        }}
                      >
                        {addingRow === r.symbol ? '...' : '+ Watch'}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpanded(expanded === r.symbol ? null : r.symbol); }}
                      style={{
                        padding: '8px 14px', fontSize: 12, background: '#1e1e1e',
                        border: '1px solid #444', color: '#aaa', borderRadius: 6, cursor: 'pointer', minHeight: 36,
                      }}
                    >
                      {expanded === r.symbol ? 'Hide ▲' : 'Details ▼'}
                    </button>
                  </MobileCardActions>
                  {expanded === r.symbol && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #222' }}>
                      <BreakdownPanelMobile result={r} checklistCtx={checklistCtx} />
                    </div>
                  )}
                </MobileCard>
              );
            })}
          </div>
        ) : (
          // ── Desktop: full table ────────────────────────────────────────────
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 1140, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', background: '#141414' }}>
                  {[
                    { label: '#',        field: 'rank' },
                    { label: 'Symbol',   field: null },
                    { label: 'Sector',   field: null },
                    { label: 'Close ₹',  field: 'close_price' },
                    { label: 'EMA',      field: null },
                    { label: 'RSI',      field: 'rsi_daily' },
                    { label: 'Vol Ratio',field: 'volume_ratio' },
                    { label: '52w High', field: 'week52_high_pct' },
                    { label: 'Score',    field: 'composite_score' },
                    { label: 'Ready',    field: null },
                    { label: 'Tags',     field: null },
                    { label: 'Action',   field: null },
                  ].map(({ label, field }) => (
                    <th
                      key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      style={{
                        padding: '10px 12px', textAlign: 'left', fontSize: 11,
                        color: sortField === field ? '#4CAF50' : '#888',
                        fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                        cursor: field ? 'pointer' : 'default',
                        userSelect: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                      {field && sortField === field && (
                        <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                      {field && sortField !== field && (
                        <span style={{ marginLeft: 4, opacity: 0.3 }}>↕</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r, i) => {
                  const volRatio = r.volume_ratio;
                  const volColor = volRatio >= 2.0 ? '#4CAF50' : volRatio >= 1.5 ? '#8BC34A' : volRatio < 0.8 ? '#f44' : '#aaa';
                  const w52pct   = r.week52_high_pct;
                  const w52Color = w52pct >= -5 ? '#4CAF50' : w52pct >= -15 ? '#FF9800' : '#888';
                  return (
                  <React.Fragment key={r.symbol}>
                    <tr
                      onClick={() => setExpanded(expanded === r.symbol ? null : r.symbol)}
                      style={{ borderBottom: expanded === r.symbol ? 'none' : '1px solid #222', cursor: 'pointer', background: expanded === r.symbol ? '#1a1f1a' : 'transparent', transition: 'background 0.1s' }}
                    >
                      <td style={{ padding: '10px 12px', color: '#555', fontSize: 12 }}>{r.rank || i + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: 'monospace' }}>{r.symbol}</td>
                      <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{r.sector || '—'}</td>
                      <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>
                        {r.close_price ? `₹${parseFloat(r.close_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>
                        {(() => {
                          const aligned = r.close_price && r.ema20 && r.ema50 && r.close_price > r.ema20 && r.ema20 > r.ema50;
                          return <span style={{ color: aligned ? '#4CAF50' : '#f44' }}>{aligned ? '✓ Aligned' : '✗ Mixed'}</span>;
                        })()}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: r.rsi_daily > 70 ? '#f44' : r.rsi_daily < 30 ? '#2196F3' : '#fff' }}>
                        {r.rsi_daily ? parseFloat(r.rsi_daily).toFixed(1) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: volColor, fontVariantNumeric: 'tabular-nums' }}>
                        {volRatio != null ? `${parseFloat(volRatio).toFixed(2)}×` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: w52Color, fontVariantNumeric: 'tabular-nums' }}>
                        {w52pct != null ? `${parseFloat(w52pct).toFixed(1)}%` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', minWidth: 120 }}>
                        <ScoreBar score={Math.round(r.composite_score || 0)} color={scoreColor(r.composite_score || 0)} />
                      </td>
                      <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                        <TradeChecklist result={r} ctx={checklistCtx} compact />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(r.setup_tags ? r.setup_tags.split(',') : r.setup_tag ? [r.setup_tag] : []).filter(Boolean).map(tag => (
                            <TagBadge key={tag} tag={tag} />
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                        {watchlisted.has(r.symbol) ? (
                          <span style={{ fontSize: 11, color: '#4CAF50', fontWeight: 600 }}>✓ Watching</span>
                        ) : (
                          <button
                            onClick={() => handleAddWatchlist(r)}
                            disabled={addingRow === r.symbol}
                            style={{
                              padding: '4px 10px', fontSize: 11, background: '#1e1e1e',
                              border: '1px solid #444', color: '#aaa', borderRadius: 4, cursor: 'pointer',
                            }}
                          >
                            {addingRow === r.symbol ? '...' : '+ Watch'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === r.symbol && <BreakdownPanel result={r} checklistCtx={checklistCtx} />}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {portfolio && (
        <PortfolioModal
          portfolio={portfolio}
          onClose={() => setPortfolio(null)}
          onApplied={loadWatchlisted}
          checklistCtx={checklistCtx}
        />
      )}
    </div>
  );
}
